// WCAG contrast helpers, so text colour can follow the background it sits on
// rather than per-theme flags (which aren't reliable for every council theme).

const parseColour = (colour) => {
  const value = colour.trim();
  const hex = value.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const digits =
      hex[1].length === 3
        ? hex[1]
            .split('')
            .map((digit) => digit + digit)
            .join('')
        : hex[1];
    return {
      r: parseInt(digits.slice(0, 2), 16),
      g: parseInt(digits.slice(2, 4), 16),
      b: parseInt(digits.slice(4, 6), 16),
      a: 1,
    };
  }
  const rgb = value.match(/^rgba?\(([^)]+)\)$/i);
  if (rgb) {
    const [r, g, b, a = 1] = rgb[1].split(',').map(Number);
    return { r, g, b, a };
  }
  return null;
};

// translucent colours are shown over the white page
const flattenOnWhite = ({ r, g, b, a }) => ({
  r: r * a + 255 * (1 - a),
  g: g * a + 255 * (1 - a),
  b: b * a + 255 * (1 - a),
});

export const getLuminance = (colour) => {
  const parsed = parseColour(colour);
  if (!parsed) return null;
  const { r, g, b } = flattenOnWhite(parsed);
  const [red, green, blue] = [r, g, b].map((channel) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
};

export const getContrastRatio = (first, second) => {
  const [lighter, darker] = [getLuminance(first), getLuminance(second)].sort(
    (a, b) => b - a
  );
  return (lighter + 0.05) / (darker + 0.05);
};

const WCAG_AA = 4.5;

const mostReadable = (background, candidates) =>
  candidates.reduce((best, candidate) =>
    getContrastRatio(background, candidate) > getContrastRatio(background, best)
      ? candidate
      : best
  );

// picks whichever theme colour reads best on the background. If none of them reach
// WCAG AA, plain black or white is used instead. Unknown formats fall back to the first.
export const readableTextOn = (background, candidates) => {
  if (getLuminance(background) === null) return candidates[0];
  const best = mostReadable(background, candidates);
  if (getContrastRatio(background, best) >= WCAG_AA) return best;
  return mostReadable(background, [best, '#000000', '#FFFFFF']);
};
